import { AiAssistantApprovalResponse } from "@/components/ai-assistant/AiAssistantApprovalCard.tsx";
import { AiAssistantCheckInCard } from "@/components/ai-assistant/AiAssistantCheckInCard.tsx";
import { AiAssistantFieldsCard } from "@/components/ai-assistant/AiAssistantFieldsCard.tsx";
import { AiAssistantMarkdown } from "@/components/ai-assistant/AiAssistantMarkdown.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { AI_ASSISTANT_FILE_URL_PREFIX } from "@/types/ai-assistant.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { Button, FontAwesomeIcon, Stack, Text } from "@skailer-com/uikit";
import { DynamicToolUIPart, getToolName, isToolUIPart, ToolUIPart, UIMessage } from "ai";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

interface AiAssistantMessageProps {
  message: UIMessage;
  onApprovalResponse: AiAssistantApprovalResponse;
}

export function AiAssistantMessage({ message, onApprovalResponse }: AiAssistantMessageProps) {
  if (message.role === "user") {
    return (
      <Stack gap={"xs"} items={"end"} className={"ml-8"}>
        {message.parts.map((part) =>
          part.type === "file" && part.url.startsWith(AI_ASSISTANT_FILE_URL_PREFIX) ? (
            <FileItem
              key={part.url}
              file={{
                id: 0,
                uuid: part.url.slice(AI_ASSISTANT_FILE_URL_PREFIX.length),
                name: part.filename ?? "",
                mime_type: part.mediaType,
                size: 0,
                file_type: ApiFilePathTypes.ai_assistant,
              }}
              showSize={false}
              className={"max-w-full"}
            />
          ) : null
        )}
        <div className={"w-full rounded-sm rounded-tr-none bg-light/50 px-3 py-2.5 shadow-xs"}>
          <Text className={"whitespace-pre-wrap break-words"}>
            {message.parts.map((part) => (part.type === "text" ? part.text : null))}
          </Text>
        </div>
      </Stack>
    );
  }

  const pendingApprovalIds = message.parts.flatMap((part) =>
    isToolUIPart(part) && part.state === "approval-requested" ? [part.approval.id] : []
  );
  const firstPendingIndex = message.parts.findIndex(
    (part) => isToolUIPart(part) && part.state === "approval-requested"
  );

  return (
    <Stack gap={"sm"}>
      {message.parts.map((part, index) => {
        if (part.type === "text" && part.text) {
          return (
            <div
              key={index}
              className={
                "html-content [&_li]:pb-2 [&_ol:not(:last-child)]:mb-5 [&_p:not(:last-child)]:mb-5 [&_ul:not(:last-child)]:mb-5"
              }
            >
              <AiAssistantMarkdown>{part.text}</AiAssistantMarkdown>
            </div>
          );
        }
        if (part.type === "reasoning" && part.text) {
          return <AiAssistantReasoning key={index} text={part.text} />;
        }
        if (isToolUIPart(part) && part.approval) {
          const Card =
            getToolName(part) === "checkin_goal" ? AiAssistantCheckInCard : AiAssistantFieldsCard;
          return (
            <Fragment key={part.toolCallId}>
              {index === firstPendingIndex && pendingApprovalIds.length > 1 && (
                <AiAssistantApprovalBatch
                  approvalIds={pendingApprovalIds}
                  onApprovalResponse={onApprovalResponse}
                />
              )}
              <Card part={part} onApprovalResponse={onApprovalResponse} />
            </Fragment>
          );
        }
        if (isToolUIPart(part)) {
          return <AiAssistantToolStatus key={part.toolCallId} part={part} />;
        }
        return null;
      })}
    </Stack>
  );
}

interface AiAssistantApprovalBatchProps {
  approvalIds: string[];
  onApprovalResponse: AiAssistantApprovalResponse;
}

function AiAssistantApprovalBatch({
  approvalIds,
  onApprovalResponse,
}: AiAssistantApprovalBatchProps) {
  const { t } = useTranslation();

  const respondAll = (approved: boolean) =>
    approvalIds.forEach((id) => onApprovalResponse({ id, approved }));

  return (
    <Stack direction={"horizontal"} gap={"sm"}>
      <Button size={"sm"} onClick={() => respondAll(true)}>
        {t("ai:assistant_confirm_all", { count: approvalIds.length })}
      </Button>
      <Button size={"sm"} variant={"secondary"} onClick={() => respondAll(false)}>
        {t("ai:assistant_cancel_all")}
      </Button>
    </Stack>
  );
}

function AiAssistantReasoning({ text }: { text: string }) {
  const { t } = useTranslation();

  return (
    <details className={"text-secondary"}>
      <summary className={"cursor-pointer select-none text-sm"}>
        {t("ai:assistant_thinking")}
      </summary>
      <Text size={"sm"} className={"mt-1 whitespace-pre-wrap border-l-2 border-dark/10 pl-3"}>
        {text}
      </Text>
    </details>
  );
}

function AiAssistantToolStatus({ part }: { part: ToolUIPart | DynamicToolUIPart }) {
  const { t } = useTranslation();
  const isDone = part.state === "output-available";
  const isFailed = part.state === "output-error";

  return (
    <Stack direction={"horizontal"} items={"center"} gap={"xs"} className={"text-secondary"}>
      <span aria-hidden={true} className={"w-4 text-center"}>
        {isDone ? (
          <FontAwesomeIcon icon={"fa-light fa-check"} className={"text-positive"} />
        ) : isFailed ? (
          <FontAwesomeIcon icon={"fa-light fa-circle-exclamation"} className={"text-danger"} />
        ) : (
          <FontAwesomeIcon icon={"fa-light fa-spinner-third fa-spin"} />
        )}
      </span>
      <Text size={"sm"}>
        {t([`ai:assistant_tool_${getToolName(part)}`, "ai:assistant_tool_default"])}
      </Text>
      {isDone && <span className={"sr-only"}>{t("ai:assistant_tool_done")}</span>}
      {isFailed && (
        <Text size={"sm"} className={"text-danger"}>
          {t("ai:assistant_tool_failed")}
        </Text>
      )}
    </Stack>
  );
}
